import { Capacitor } from '@capacitor/core'

/*
 | =============================================================================
 | iOS scroll-top after navigation (Capacitor / WKWebView only)
 | =============================================================================
 | WKWebView preserves scroll position across SPA route changes and re-applies
 | a cached scrollTop asynchronously — at an unpredictable time after the new
 | page mounts. A one-shot scrollTo loses this race on pages whose content
 | height settles late (e.g. async data / feature flags grow the page after we
 | already scrolled to top), so WKWebView restores the old offset afterwards.
 |
 | To fix this reliably we:
 |  1. Disable browser-native scroll restoration so WKWebView stops caching/
 |     re-applying the previous scrollTop where possible.
 |  2. Run a short watchdog after navigation that snaps back to top on every
 |     frame for a brief window, beating any late WKWebView restore. It bows
 |     out immediately on the first real user input so we never fight an
 |     intentional scroll.
 |
 | Scope: iOS native build only — web (incl. PWA) keeps browser-default
 | scroll behaviour, so back/forward scroll restoration still works.
 */
const WATCHDOG_MS = 1000

export default defineNuxtPlugin(nuxtApp => {
  if (!Capacitor.isNativePlatform() || Capacitor.getPlatform() !== 'ios') return

  // Stop WKWebView from auto-restoring scroll on navigation
  if ('scrollRestoration' in window.history) {
    window.history.scrollRestoration = 'manual'
  }

  const router = useRouter()

  router.afterEach((to, from) => {
    if (to.path === from.path) return // hash-only / same path

    nuxtApp.hooks.hookOnce('page:finish', () => {
      let cancelled = false
      const cancel = () => {
        cancelled = true
      }
      // First intentional user input wins — stop correcting scroll.
      const opts = { passive: true, capture: true } as const
      window.addEventListener('touchstart', cancel, opts)
      window.addEventListener('wheel', cancel, opts)
      window.addEventListener('keydown', cancel, opts)

      // `html { scroll-smooth }` would animate every correction into a visible
      // crawl, so force instant jumps for the duration of the watchdog.
      const root = document.documentElement
      const prevBehavior = root.style.scrollBehavior
      root.style.scrollBehavior = 'auto'

      const cleanup = () => {
        root.style.scrollBehavior = prevBehavior
        window.removeEventListener('touchstart', cancel, opts)
        window.removeEventListener('wheel', cancel, opts)
        window.removeEventListener('keydown', cancel, opts)
      }

      const start = performance.now()
      const tick = () => {
        if (cancelled) return cleanup()
        if (window.scrollY !== 0) window.scrollTo(0, 0) // instant: scroll-behavior forced to auto above
        if (performance.now() - start < WATCHDOG_MS) {
          requestAnimationFrame(tick)
        } else {
          cleanup()
        }
      }
      requestAnimationFrame(tick)
    })
  })
})
